import React from 'react'

import styled from '@emotion/styled'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { breakpoints } from '@cash-design-system/react'
import { IdentityVerificationBlocker } from '@cash-web/protos/squareup/franklin/blockers/identity_verification_blocker.pb'
import { IdentityVerificationBlockerSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationVerifyIdentity } from '@cash-web/shared-data-access-api/react-query/useMutationVerifyIdentity'
import { BirthdateInput, NameInput, SsnInput } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BaseBlocker, BaseBlockerFormFields, BlockerProps } from '../BaseBlocker/BaseBlocker'

interface Props extends BlockerProps {
  blocker: IdentityVerificationBlocker
  supplement?: IdentityVerificationBlockerSupplement
}

type IdvFields = BaseBlockerFormFields & {
  name: string
  birth_date: string
  ssn: string
  ssn_last_four: string
}

const InlineInputWrapper = styled.div`
  display: grid;
  column-gap: 0.9375rem;
  row-gap: 0.9375rem;
  grid-template-columns: repeat(1, 1fr);

  & > *:last-child {
    grid-column: span 1;
  }

  ${breakpoints.tablet} {
    grid-template-columns: repeat(2, 1fr);

    & > *:last-child {
      grid-column: auto;
    }
  }
`

export function BlockerIdentityVerification({ blocker, supplement, skippable }: Props): React.ReactElement {
  const { t } = useTranslation()
  // Note: The `shouldUnregister` option is set to true so that the form is reset when the component
  // is unmounted. This is needed when we get a new blocker from the server in an error state and this form is reloaded.
  // if this is not true then we would send old data like the last four ssn from the previous blocker.
  const form = useForm<IdvFields>({ mode: 'onTouched', shouldUnregister: true })
  const flow = useFlowContext()
  const { handleBlockerResponse, genericError } = useBlockerResponseHandler()

  const { mutateAsync: verifyIdentity } = useMutationVerifyIdentity({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })

  const isMultiPartForm = Object.values(blocker).filter(enabled => enabled).length > 1

  // Note: The titles specified in `supplement` are not being used in the title so that the web
  // can have more control of the title and subtitle.
  let title = t('Let’s verify your identity', { context: 'Title text.' })
  if (!isMultiPartForm) {
    if (blocker.requires_full_ssn) {
      title = t('Enter your full Social Security Number', { context: 'Title text.' })
    } else if (blocker.requires_last_four_ssn) {
      title = t('Enter the last 4 digits of your social security number', { context: 'Title text.' })
    } else if (blocker.requires_birthdate) {
      title = t('Enter your date of birth', { context: 'Title text.' })
    } else if (blocker.requires_full_name) {
      title = t('Enter your full legal name', { context: 'Title text.' })
    }
  }
  const subtitle = t('We need to collect some info to confirm that you’re really you.', { context: 'Subtitle text.' })

  // There is no support for address fields yet since these
  // were not required for the login flow.
  if (blocker.requires_address) {
    throw Error('Address not implemented in BlockerIdentityVerification')
  }

  async function onSubmit(data: IdvFields) {
    await handleBlockerResponse({
      promisedBlockerUpdate: verifyIdentity({
        ...data,
        transfer_token: flow.active?.transferToken,
        payment_tokens: flow.active?.paymentTokens,
      }),
      errors: {
        CONCURRENT_MODIFICATION: genericError,
        INVALID: genericError,
        INVALID_IDENTITY: t(`We couldn't confirm your identity, please verify your information.`, {
          context: 'Error to the user when the response status is INVALID_IDENTITY',
        }),
        FAILURE: t('Unfortunately we were unable to verify your identity.', {
          context: 'Error to the user when the response status is FAILURE',
        }),
      },
    })
  }

  const helpModal = supplement?.additional_help_items ? { helpItems: supplement.additional_help_items } : undefined

  const hasSsn = blocker.requires_full_ssn || blocker.requires_last_four_ssn
  const hasBirthdateAndSsn = blocker.requires_birthdate && hasSsn

  const birthdateAndSsnInputs = (
    <>
      {blocker.requires_birthdate && <BirthdateInput isRequired name="birth_date" />}
      {blocker.requires_full_ssn && <SsnInput variant="FULL" name="ssn" isRequired />}
      {blocker.requires_last_four_ssn && !blocker.requires_full_ssn && (
        <SsnInput variant="LAST_FOUR" name="ssn_last_four" isRequired />
      )}
    </>
  )

  return (
    <BaseBlocker
      mainText={title}
      secondaryText={subtitle}
      form={form}
      onSubmit={onSubmit}
      skippable={skippable}
      helpModal={helpModal}
    >
      {blocker.requires_full_name && (
        <NameInput
          autoFocus
          name={'name'}
          label={t('Full legal name', { context: 'label on input asking for full legal name' })}
        />
      )}
      {/* If birthdate and ssn are present, they should be next to each other on desktop screens */}
      {hasBirthdateAndSsn && <InlineInputWrapper>{birthdateAndSsnInputs}</InlineInputWrapper>}
      {!hasBirthdateAndSsn && birthdateAndSsnInputs}
    </BaseBlocker>
  )
}
